import React from 'react';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../ui/sheet';
import { SlidersHorizontal } from 'lucide-react';

// Filter content extracted to avoid nested component definition
const FilterContent = ({
  manufacturers,
  productTypes,
  selectedManufacturers,
  selectedTypes,
  searchQuery,
  onManufacturerChange,
  onTypeChange,
  onSearchChange,
  onClearFilters,
  hasActiveFilters,
}) => (
  <div className="space-y-6">
    {/* Search */}
    <div>
      <Label htmlFor="search" className="text-sm font-medium mb-2 block">
        Search Products
      </Label>
      <Input
        id="search"
        placeholder="Search by name..."
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        className="w-full"
      />
    </div>

    <Separator />

    {/* Manufacturers */}
    <div>
      <h4 className="text-sm font-medium mb-3">Manufacturer</h4>
      <ScrollArea className="h-[200px] pr-4">
        <div className="space-y-2">
          {manufacturers.map((manufacturer) => (
            <div key={manufacturer} className="flex items-center space-x-2">
              <Checkbox
                id={`manufacturer-${manufacturer}`}
                checked={selectedManufacturers.includes(manufacturer)}
                onCheckedChange={(checked) => onManufacturerChange(manufacturer, checked)}
              />
              <Label
                htmlFor={`manufacturer-${manufacturer}`}
                className="text-sm cursor-pointer"
              >
                {manufacturer}
              </Label>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>

    <Separator />

    {/* Product Types */}
    <div>
      <h4 className="text-sm font-medium mb-3">Product Type</h4>
      <ScrollArea className="h-[200px] pr-4">
        <div className="space-y-2">
          {productTypes.map((type) => (
            <div key={type} className="flex items-center space-x-2">
              <Checkbox
                id={`type-${type}`}
                checked={selectedTypes.includes(type)}
                onCheckedChange={(checked) => onTypeChange(type, checked)}
              />
              <Label
                htmlFor={`type-${type}`}
                className="text-sm cursor-pointer"
              >
                {type}
              </Label>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>

    {/* Clear Filters */}
    {hasActiveFilters && (
      <>
        <Separator />
        <Button
          variant="outline"
          onClick={onClearFilters}
          className="w-full"
        >
          Clear All Filters
        </Button>
      </>
    )}
  </div>
);

export const FiltersSidebar = ({
  manufacturers,
  productTypes,
  selectedManufacturers,
  selectedTypes,
  searchQuery,
  onManufacturerChange,
  onTypeChange,
  onSearchChange,
  onClearFilters,
}) => {
  const hasActiveFilters = selectedManufacturers.length > 0 || selectedTypes.length > 0 || searchQuery;

  const filterProps = {
    manufacturers,
    productTypes,
    selectedManufacturers,
    selectedTypes,
    searchQuery,
    onManufacturerChange,
    onTypeChange,
    onSearchChange,
    onClearFilters,
    hasActiveFilters,
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:block w-72 flex-shrink-0">
        <div className="sticky top-24 bg-card rounded-xl border border-border/50 p-6 shadow-md">
          <h3 className="text-lg font-semibold mb-6">Filter Products</h3>
          <FilterContent {...filterProps} />
        </div>
      </aside>

      {/* Mobile Filter Sheet */}
      <div className="lg:hidden">
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="outline" className="gap-2">
              <SlidersHorizontal className="w-4 h-4" />
              Filters
              {hasActiveFilters && (
                <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
                  {selectedManufacturers.length + selectedTypes.length + (searchQuery ? 1 : 0)}
                </span>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[300px] sm:w-[350px]">
            <SheetHeader>
              <SheetTitle>Filter Products</SheetTitle>
            </SheetHeader>
            <div className="mt-6">
              <FilterContent {...filterProps} />
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
};
